import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";

import { getDeal, listManagerDeals, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import {
  ManagerDealReview,
  reviewSubtitle,
  reviewTitle,
} from "@/components/deals/ManagerDealReview";

/**
 * Standalone CM/RA/RM review page — the deep-link fallback for a deal review
 * (the primary UX is the right-hand drawer on the ledger, CHP-223). Renders the
 * same `ManagerDealReview`; here a decision redirects back to the dashboard
 * after the toast hold (in the drawer it closes instead).
 */
export function ManagerDealDecisionRoute() {
  const { accessToken } = useCurrentUser();
  const navigate = useNavigate();
  const { dealId } = useParams({ from: "/dashboard/manager/deals/$dealId" });

  const deal = useQuery<Deal>({
    queryKey: ["deals", "detail", dealId, accessToken],
    queryFn: () => getDeal(accessToken!, dealId),
    enabled: !!accessToken && !!dealId,
  });

  // The detail response carries no channel-partner name; reuse the manager
  // ledger row (cached from the dashboard) for the subtitle + decision copy.
  const managerList = useQuery({
    queryKey: ["deals", "manager-list", accessToken],
    queryFn: () => listManagerDeals(accessToken!, "all"),
    enabled: !!accessToken,
  });
  const row = managerList.data?.find((r) => r.id === dealId);

  if (deal.isLoading)
    return (
      <p role="status" className="container py-6">
        Loading…
      </p>
    );
  if (deal.error)
    return (
      <div className="container py-6">
        <p role="alert">Couldn't load this deal.</p>
        <p className="mt-2 text-sm">
          <Link to="/dashboard/manager/deals">Back to Deals</Link>
        </p>
      </div>
    );
  if (!deal.data) return null;

  const d = deal.data;

  return (
    <div className="container py-6">
      <p className="mb-4 text-sm text-muted-foreground">
        <Link to="/dashboard/manager/deals">← Back to Deals</Link>
      </p>

      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">{reviewTitle(d)}</h1>
        <p className="text-sm text-muted-foreground">
          {reviewSubtitle(d, row?.channel_partner_company, row?.lead_bdm_name)}
        </p>
      </header>

      <ManagerDealReview
        deal={d}
        partnerName={row?.channel_partner_company ?? null}
        leadName={row?.lead_bdm_name ?? null}
        onDone={() => void navigate({ to: "/dashboard/manager/deals" })}
      />
    </div>
  );
}
